/**
 * useSession
 *
 * Primary read hook for session state + actions.
 * Consumers use this to check session status and trigger actions.
 *
 * Wallet-touching actions (connect/confirmSign/cancel/switchWallet/
 * reconnect/logout/disconnect) need `SessionWalletOps` — this hook binds
 * them to the current `useBrowserWallet()` instance via a ref, so the
 * exposed action signatures (and consumer code) are unchanged and the
 * action identities stay stable across wallet re-renders.
 */

import { useCallback, useEffect, useRef } from "react";
import { useBrowserWallet } from "@/features/wallet";
import { useSessionManager } from "../session-manager";
import type {
  SessionStatus,
  AuthSubState,
  SessionWalletType,
  SessionWalletOps,
} from "../types";

interface UseSessionReturn {
  // State
  status: SessionStatus;
  walletAddress: string | null;
  walletType: SessionWalletType | null;
  walletId: string | null;
  authSubState: AuthSubState | null;
  challengeMessage: string | null;
  authError: string | null;
  reconnectError: string | null;
  previousStatus: SessionStatus | null;

  // Derived
  isActive: boolean;
  isAuthenticating: boolean;
  isRestoring: boolean;

  // Actions
  connect: (address: string) => Promise<void>;
  confirmSign: () => Promise<void>;
  retry: () => Promise<void>;
  cancel: () => void;
  switchWallet: (address: string) => Promise<void>;
  reconnect: () => Promise<void>;
  logout: () => Promise<void>;
  disconnect: () => void;
}

export function useSession(): UseSessionReturn {
  const status = useSessionManager((s) => s.status);
  const walletAddress = useSessionManager((s) => s.walletAddress);
  const walletType = useSessionManager((s) => s.walletType);
  const walletId = useSessionManager((s) => s.walletId);
  const authSubState = useSessionManager((s) => s.authSubState);
  const challengeMessage = useSessionManager((s) => s.challengeMessage);
  const authError = useSessionManager((s) => s.authError);
  const reconnectError = useSessionManager((s) => s.reconnectError);
  const previousStatus = useSessionManager((s) => s.previousStatus);
  const connectRaw = useSessionManager((s) => s.connect);
  const confirmSignRaw = useSessionManager((s) => s.confirmSign);
  const retry = useSessionManager((s) => s.retry);
  const cancelRaw = useSessionManager((s) => s.cancel);
  const switchWalletRaw = useSessionManager((s) => s.switchWallet);
  const reconnectRaw = useSessionManager((s) => s.reconnect);
  const logoutRaw = useSessionManager((s) => s.logout);
  const disconnectRaw = useSessionManager((s) => s.disconnect);

  // Latest wallet ops without churning the bound actions' identities. The
  // ref is written after commit, not during render; every reader below is an
  // event-time callback, which runs after the commit that refreshed it.
  const wallet = useBrowserWallet();
  const walletRef = useRef<SessionWalletOps>(wallet);
  useEffect(() => {
    walletRef.current = wallet;
  });

  const connect = useCallback(
    (address: string) => connectRaw(address, walletRef.current),
    [connectRaw],
  );
  const confirmSign = useCallback(
    () => confirmSignRaw(walletRef.current),
    [confirmSignRaw],
  );
  const cancel = useCallback(
    () => cancelRaw(walletRef.current),
    [cancelRaw],
  );
  const switchWallet = useCallback(
    (address: string) => switchWalletRaw(address, walletRef.current),
    [switchWalletRaw],
  );
  const reconnect = useCallback(
    () => reconnectRaw(walletRef.current),
    [reconnectRaw],
  );
  const logout = useCallback(
    () => logoutRaw(walletRef.current),
    [logoutRaw],
  );
  const disconnect = useCallback(
    () => disconnectRaw(walletRef.current),
    [disconnectRaw],
  );

  return {
    status,
    walletAddress,
    walletType,
    walletId,
    authSubState,
    challengeMessage,
    authError,
    reconnectError,
    previousStatus,
    isActive: status === "active",
    isAuthenticating: status === "authenticating",
    isRestoring: status === "restoring",
    connect,
    confirmSign,
    retry,
    cancel,
    switchWallet,
    reconnect,
    logout,
    disconnect,
  };
}
